import { UserDetail } from '@lovoldsystem/core.client'
import { Box, darken, Grid, useTheme } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetCustomerUsers } from 'src/api/customer'
import { useAllUsers, useGetSystemProjectAdmins } from 'src/api/user'
import { AppForm, AppFormProps, FormFileCoreUpload, FormSelectField, FormTextField } from 'src/components/FormCore'
import { MainButton } from 'src/components/MainButton'
import { IFile, IMooringType, IUser } from 'src/generated-types'
import { useZodForm } from 'src/hooks/zod-form'
import { EdgeCase, getUserDetail } from 'src/utils/getUserDetails'
import { z } from 'zod'
import BasicDatePicker from 'src/components/DateTimePicker'
import { useGetMooringAnalysisProviders } from 'src/api/supplier'
import SetupFacilityForm from './SetupFacilityForm'
import { toast } from 'react-toastify'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { CancelButton } from 'src/components/CancelButton'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { CreateContactDialog } from './CreateContactDialog'
import { createFilterOptions } from '@mui/material/Autocomplete'
import { ProjectType } from 'src/enums'
import { IProjectType } from 'src/generated-types/project-type'
interface ProjectFormProps extends AppFormProps {
  title: string
  customerId: EntityId
  localityId: EntityId
  onSubmit: (form: any) => void
  isLoading?: boolean
  file?: IFile
  onStepBack?: () => void
  isProjectUpdate?: boolean // to differentiate between project form with and without setupfacility form
  showMapSVG?: boolean // to differentiate whether to show map svg in setupfacility form or not
}
const ProjectForm: React.FC<ProjectFormProps> = ({
  onSubmit,
  onCancel,
  initialValues,
  title,
  customerId,
  isLoading,
  localityId,
  onStepBack,
  isProjectUpdate = false,
  showMapSVG = true,
}) => {
  const { t: translate } = useTranslation(['projects', 'common'])
  const [hasPendingChanges, setHasPendingChanges] = useState(false)
  const theme = useTheme()
  const { showWarningDialog, showDialog } = useDialogCtx()
  const [newContacts, setNewContacts] = useState<any[]>([])
  const filterOurContact = createFilterOptions<any>()
  const positiveOptionalNumber = z
    .union([z.string(), z.number()])
    .optional()
    .nullable()
    .transform(val => (val === '' || val === undefined || val === null ? null : Number(val)))
    .refine(val => val === null || (!Number.isNaN(val) && val > 0), translate('mustBePositive', { ns: 'common' }))
  const projectSchema = z
    .object({
      id: initialValues?.id ? z.number() : z.number().optional(),
      name: z.string().min(1, translate('project-name-required', { ns: 'projects' })),
      description: z.string().optional().nullable(),
      ourContactId: z.string().min(1),
      ourContact: z.object({
        id: z.string().min(1, { message: translate('required', { ns: 'common' }) }),
      }),
      customerContactId: z.string().nullable().optional(),
      customerContact: z
        .object({
          id: z.string(),
        })
        .optional()
        .nullable(),
      customerId: z.number(),
      fileIdsWithTitle: z.array(z.object({ fileId: z.number(), title: z.string() })).optional(),
      type: z.nativeEnum(IMooringType, {}).optional().nullable(),
      noOfCages: z
        .union([z.string(), z.number()])
        .optional()
        .nullable()
        .transform(val => (val === '' || val === undefined || val === null ? undefined : Number(val)))
        .transform(val => (val === undefined || Number.isNaN(val) ? undefined : val)),
      noOfPens: z
        .union([z.string(), z.number()])
        .optional()
        .nullable()
        .transform(val => (val === '' || val === undefined || val === null ? undefined : Number(val)))
        .refine(val => val === undefined || val >= 0, translate('mustBeZeroOrPositive', { ns: 'common' })),
      noOfRows: z
        .union([z.string(), z.number()])
        .optional()
        .nullable()
        .transform(val => (val === '' || val === undefined || val === null ? undefined : Number(val)))
        .transform(val => (val === undefined || Number.isNaN(val) ? undefined : val)),
      positions: z.array(z.any()).optional().nullable(),
      serviceRow: z.boolean().optional().nullable(),
      localityId: z.number(),
      deliverDate: z.union([z.date(), z.string()]).optional().nullable(),
      orderDate: z.union([z.date(), z.string()]).optional().nullable(),
      mooringAnalysisSupplierId: z.number().optional().nullable(),
      mooringAnalysisId: z.string().optional().nullable(),
      projectType: z.nativeEnum(IProjectType, {}).optional().nullable(),
      gridLineWidthInMeters: positiveOptionalNumber,
      gridLineHeightInMeters: positiveOptionalNumber,
    })
    .superRefine((values, ctx) => {
      // Allow submit when type is not selected.
      if (values.type == null) {
        return
      }

      if (values.type === IMooringType.Barge) {
        return
      }

      if (values.noOfCages !== undefined && values.noOfCages <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['noOfCages'],
          message: translate('mustBePositive', { ns: 'common' }),
        })
      }

      if (values.type !== IMooringType.LadderCage && values.noOfRows !== undefined && values.noOfRows <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['noOfRows'],
          message: translate('mustBePositive', { ns: 'common' }),
        })
      }

      if (values.type === IMooringType.FrameCage) {
        if (!values.noOfPens || values.noOfPens <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['noOfPens'],
            message: translate('mustBePositive', { ns: 'common' }),
          })
        } else if (values.noOfCages !== undefined && values.noOfPens > values.noOfCages) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['noOfPens'],
            message: translate('pens-cannot-exceed-cages', { ns: 'projects' }),
          })
        }
      }
    })
  const form = useZodForm(projectSchema, { defaultValues: initialValues ?? {} })

  const { data: customerUsers } = useGetCustomerUsers(customerId as EntityId, undefined, !!customerId)
  const { data: ourUsers } = useAllUsers('', undefined)
  const { data: suppliers } = useGetMooringAnalysisProviders()
  const { data: projectAdmins } = useGetSystemProjectAdmins()

  const customerUsersWithAdmins = useMemo(() => {
    return [...(customerUsers ?? []), ...(projectAdmins ?? [])]
  }, [customerUsers, projectAdmins])

  const handleMainButtonClick = async () => {
    initialValues?.id && form.setValue('id', initialValues.id)
    form.setValue('customerId', customerId as number)
    form.setValue('localityId', localityId as number)

    const type = form.getValues('type')
    const isBargeOrLadder = type === IMooringType.Barge || type === IMooringType.LadderCage

    if (isBargeOrLadder) {
      await form.handleSubmit(onSubmit)()
      return
    }

    const rows = form.getValues('noOfRows')
    const cages = form.getValues('noOfCages')

    if (!rows || rows <= 0 || !cages) {
      await form.handleSubmit(onSubmit)()
      return
    }

    if (cages % rows !== 0) {
      toast.error(translate('cages-must-be-multiple-of-rows', { ns: 'projects' }))
      return
    }

    await form.handleSubmit(onSubmit)()
  }

  const handleChange = (key, value) => {
    form.setValue(key, value, { shouldValidate: true })
    form.setValue(`${key}Id` as 'ourContactId' | 'customerContactId', value?.id, { shouldValidate: true })
  }

  const onCreateUser = (user: IUser) => {
    setNewContacts(prev => [...prev, user])
    handleChange('ourContact', user)
  }

  const handleOurContactChange = async (_, value) => {
    if (value && value.inputValue) {
      showDialog(
        CreateContactDialog,
        {
          componentProps: { initialName: value.inputValue, onCreateUser },
          opts: { maxWidth: 'sm', fullWidth: true },
        },
        undefined,
        true,
      )
    } else handleChange('ourContact', value)
  }

  const toTitleCase = (str: string) => {
    return str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase())
  }
  useEffect(() => {
    if (initialValues && isProjectUpdate) {
      if (initialValues.ourContactId && ourUsers) {
        form.setValue('ourContact', ourUsers.find(u => u.id === initialValues.ourContactId)!)
      }
      if (initialValues.customerContactId && customerUsersWithAdmins) {
        form.setValue('customerContact', customerUsersWithAdmins!.find(u => u.id === initialValues.customerContactId)!)
      }
    }
  }, [initialValues, isProjectUpdate, ourUsers, customerUsersWithAdmins, form])

  type ProjectOption = {
    id: string
    label: string
  }

  const pendingChangesPop = (hasChanges: boolean, callBackFn: () => void) => {
    if (hasChanges) {
      showWarningDialog(
        translate('unsavedChangesWarning', { ns: 'position' }),
        translate('unsavedChangesWarningSubtext', { ns: 'position' }),
        false,
        [
          {
            text: translate('cancel', { ns: 'common' }),
            callback: warningPop => warningPop(),
            variant: 'outlined',
            sx: {
              backgroundColor: getThemeColor(theme, MUIThemeColors.white),
              '&:hover': { backgroundColor: darken(getThemeColor(theme, MUIThemeColors.white), 0.2) },
            },
          },
          {
            text: translate('discard', { ns: 'common' }),
            callback: warningPop => {
              warningPop()
              callBackFn()
            },
          },
        ],
      )
    } else {
      callBackFn()
    }
  }

  const projectTypes = useMemo(() => {
    return Object.values(IProjectType)
      .filter(v => isNaN(Number(v)))
      .map(t => IProjectType[t])
  }, [])

  const mooringAnalysisProviderId = form.watch('mooringAnalysisSupplierId')

  return (
    <AppForm form={form} hasInitialValues={initialValues} title={title}>
      <FormSelectField
        name="name"
        label={translate('project-name', { ns: 'projects' })}
        freeSolo
        data={[] as ProjectOption[]}
        getOptionLabel={option => (typeof option === 'string' ? option : option.label)}
        onChange={(_, value) => {
          let finalValue = ''

          if (typeof value === 'string') {
            finalValue = value
          } else if (Array.isArray(value)) {
            const first = value[0]
            finalValue = typeof first === 'string' ? first : first?.label ?? ''
          } else if (value) {
            finalValue = value.label
          }
          form.setValue('name', finalValue)
          return finalValue
        }}
        filterOptions={(_, params) => {
          if (
            !(params.inputValue.includes(`${ProjectType.Barge}`) || params.inputValue.includes(`${ProjectType.Other}`))
          ) {
            const inputValue = params.inputValue?.trim()

            if (!inputValue) {
              return []
            }

            return [
              {
                id: `01-${inputValue}`,
                label: toTitleCase(`${inputValue} ${ProjectType.Barge}`),
              },
              {
                id: `02-${inputValue}`,
                label: toTitleCase(`${inputValue} ${ProjectType.Other}`),
              },
            ]
          } else {
            form.setValue('name', toTitleCase(params.inputValue))
            return []
          }
        }}
      />
      <FormTextField
        name="description"
        label={translate('project-description', { ns: 'projects' })}
        multiline
        rows={4}
      />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormSelectField
            name="ourContact"
            label={translate('our-contact-person', { ns: 'projects' })}
            data={[...newContacts, ...(ourUsers ?? [])]}
            getOptionLabel={option => {
              if (typeof option === 'string') return option
              if (option.inputValue) return option.inputValue
              return getUserDetail(option, UserDetail.NameAndEmail)
            }}
            renderOption={(props: any, option) => (
              <Box {...props} key={option.id ?? option.inputValue}>
                {option.inputValue
                  ? translate('create-option', { ns: 'projects', name: option.inputValue })
                  : getUserDetail(option, UserDetail.NameAndEmail, EdgeCase.ReturnEmailForNewUser)}
              </Box>
            )}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            onChange={handleOurContactChange}
            filterOptions={(options, params) => {
              const filtered = filterOurContact(options, params)
              const { inputValue } = params
              const isExisting = options.some(
                option => getUserDetail(option, UserDetail.NameAndEmail).toLowerCase() === inputValue.toLowerCase(),
              )
              if (inputValue !== '' && !isExisting) {
                filtered.push({
                  inputValue,
                  id: `create-${inputValue}`,
                })
              }
              return filtered
            }}
            freeSolo
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormSelectField
            name="customerContact"
            label={
              customerUsersWithAdmins?.length === 0
                ? translate('no-customer-contact-person', { ns: 'projects' })
                : translate('customer-contact-person', { ns: 'projects' })
            }
            data={customerUsersWithAdmins}
            getOptionLabel={option => getUserDetail(option, UserDetail.NameAndEmail)}
            renderOption={(props: any, option) => (
              <Box {...props} key={option.id}>
                {getUserDetail(option, UserDetail.NameAndEmail, EdgeCase.ReturnEmailForNewUser)}
              </Box>
            )}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            onChange={(_, v) => handleChange('customerContact', v)}
            disabled={customerUsersWithAdmins?.length === 0}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={4}>
          <FormSelectField
            name="projectType"
            data={projectTypes}
            label={translate('project-type', { ns: 'projects' })}
            getOptionLabel={option => translate(IProjectType[option], { ns: 'projects' })}
            renderOption={(props: any, option) => (
              <Box {...props} key={option}>
                {translate(IProjectType[option], { ns: 'projects' })}
              </Box>
            )}
            sx={{ width: '100%' }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <BasicDatePicker name="orderDate" label={translate('order-date', { ns: 'projects' })} allowNullable />
        </Grid>
        <Grid item xs={12} sm={4}>
          <BasicDatePicker name="deliverDate" label={translate('deliver-date', { ns: 'projects' })} allowNullable />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormTextField name="mooringAnalysisId" label={translate('mooring-analysis-id', { ns: 'projects' })} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormSelectField
            name="mooringAnalysisSupplierId"
            label={translate(suppliers?.length === 0 ? 'no-mooring-analysis-supplier' : 'mooring-analysis-supplier', {
              ns: 'projects',
            })}
            data={suppliers ?? []}
            getOptionLabel={option => (typeof option === 'object' ? option.name : '') ?? ''}
            renderOption={(props: any, option) => (
              <Box {...props} key={option.id}>
                {option.name}
              </Box>
            )}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            onChange={(_, v: any) => {
              form.setValue('mooringAnalysisSupplierId', v?.id ?? null)
              return v?.id ?? null
            }}
            value={suppliers?.find(s => s.id === mooringAnalysisProviderId) ?? null}
            disabled={suppliers?.length === 0}
          />
        </Grid>
      </Grid>
      <FormFileCoreUpload
        label={translate('fileName', { ns: 'common' })}
        name="fileIdsWithTitle"
        mapFileKey="id"
        height="150px"
        multiple={true}
        enableTitleEdit={true}
        userMessage={translate('locality-report', { ns: 'document' })}
      />
      {initialValues?.id && !isProjectUpdate && (
        <SetupFacilityForm
          form={form}
          existingPositions={initialValues?.positions}
          project={initialValues}
          setHasPendingChanges={setHasPendingChanges}
          hasPendingChanges={hasPendingChanges}
          showMapSVG={showMapSVG}
        />
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Box display={'flex'} gap={2}>
          {isProjectUpdate || !initialValues?.id ? (
            <GridActionButton
              border={1}
              sx={{ width: '120px', height: '44px' }}
              onClick={() => {
                pendingChangesPop(hasPendingChanges, () => {
                  onStepBack?.()
                  onCancel?.()
                })
              }}
              buttonText={translate('back', { ns: 'common' })}
            />
          ) : (
            <CancelButton onClick={() => pendingChangesPop(hasPendingChanges, () => onCancel?.())} />
          )}
          <MainButton
            key="project-form-button"
            variant="contained"
            disabled={isLoading}
            onClick={() => pendingChangesPop(hasPendingChanges, () => handleMainButtonClick())}
            loading={isLoading}
          >
            {initialValues?.id
              ? translate('update-project', { ns: 'projects' })
              : translate('add-project', { ns: 'projects' })}
          </MainButton>
        </Box>
      </Box>
    </AppForm>
  )
}

export default ProjectForm
